import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { EMPTY, Observable, switchMap } from 'rxjs';
import { AlertService } from '../alerts/alerts.service';
import { DataDogService } from '../configuration/datadog/services/data-dog.service';
import { OriginsEnum } from '../configuration/enums/origins.enum';
import { RoutesEnum } from '../configuration/enums/routes.enum';
import { AttributeTypeEnum, CustomKeyEnum } from '../configuration/enums/tracking-data-dog.enum';
import { SessionStorageService } from '../configuration/services/session-storage.service';
import { LoggedAreaTokenExpiredError } from '../errors/logged-area-token-expired.error';
import { AuthService } from '../features/logged-area/auth/services/auth.service';
import { CacheKeys } from '../services/cache/cache-keys.enum';
import { CacheLocalStorageService } from '../services/cache/cache-local-storage.service';
import { AppState } from '../store';
import { setLoadingDisabled } from '../store/states/config/config.actions';
import { selectConfigOrigin } from '../store/states/config/config.selectors';
import { InterceptorConfigService } from './interceptor-config.service';
import { AuthState } from '../features/logged-area/auth/states/auth.state';

type TokenStatus = 'refresh' | 'expired' | 'valid';

@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
  constructor(
    private readonly store: Store<AppState>,
    private readonly injector: Injector,
    private readonly interceptorConfig: InterceptorConfigService,
    private readonly cacheLocalService: CacheLocalStorageService,
    private readonly sessionStorageService: SessionStorageService,
    private readonly alertService: AlertService
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const origin = this.store.selectSignal(selectConfigOrigin);

    if (origin() !== OriginsEnum.LOGGED_AREA || this.interceptorConfig.isUrlExcluded(request.url))
      return next.handle(request);

    const authState = this.injector.get(AuthState);
    const authService = this.injector.get(AuthService);

    const tokenStatus = this.getTokenStatus(authState.currentUser().authInfo?.expiration as string);
    switch (tokenStatus) {
      case 'refresh':
        return authService.updateToken().pipe(
          switchMap(() => {
            const clonedRequest = request.clone({
              url: request.url,
            });
            return next.handle(clonedRequest);
          })
        );
      case 'expired':
        this.openAlertAndLogoff();
        return EMPTY;
      default:
        return next.handle(request);
    }
  }

  private openAlertAndLogoff() {
    const router = this.injector.get(Router);
    const datadogService = this.injector.get(DataDogService);
    datadogService.createTrackingAttribute(
      AttributeTypeEnum.ModalToExpiredUserToken,
      CustomKeyEnum.LoggedArea
    );
    this.cacheLocalService.delete(CacheKeys.AREA_LOGGED_TOKEN);
    this.sessionStorageService.remove(CacheKeys.AREA_LOGGED_TOKEN);
    this.alertService.present({
      ...LoggedAreaTokenExpiredError.getAlertData(),
      actionOk: () => {
        router.navigate(['/' + RoutesEnum.LOGGED_AREA], { replaceUrl: true });
        this.store.dispatch(setLoadingDisabled());
      },
    });
  }

  private getTokenStatus(expiration: string): TokenStatus {
    const secondsUntilExpiration = this.secondsUntilExpiration(expiration);
    const THREE_MINUTES = 180;
    if (secondsUntilExpiration > THREE_MINUTES) return 'valid';
    if (secondsUntilExpiration >= 0) return 'refresh';

    return 'expired';
  }

  private secondsUntilExpiration(expiration: string): number {
    const expirationMoment = moment(expiration);
    const now = moment();
    return expirationMoment.diff(now, 'seconds');
  }
}
